import {
  CheckCircledIcon,
  ExclamationTriangleIcon,
} from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Card,
  Container,
  DataList,
  Flex,
  Grid,
  Text,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { DialogButton } from 'components/common/dialogs/button';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { SendRemoteCalRequest } from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/camera-calibration';
import { RemoteVisionCalibrationStageHeader } from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/stage-header';
import useVisionRemoteStore from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/store/useVisionRemoteStore';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import {
  feetFloatToString,
  FT_TO_INCHES,
  MM_TO_FT,
  round,
} from 'lib_ts/classes/math.utilities';
import { VisionHelper } from 'lib_ts/classes/vision.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IAdminMachine } from 'lib_ts/interfaces/i-machine';
import { isNil } from 'lodash';
import { PropsWithChildren, useCallback, useState } from 'react';

const DataCard = ({
  title,
  value,
  delta,
}: {
  title: string;
  value?: string;
  delta?: number;
}) => {
  // Only show badge if delta is greater than 1 inch
  const showDelta = delta && Math.abs(delta) * FT_TO_INCHES >= 1;
  return (
    <Card className="no-padding">
      <Flex direction="column" gap={RADIX.FLEX.GAP.XS} p={RADIX.FLEX.PAD.MD}>
        <Text color={RADIX.COLOR.SECONDARY}>{title}</Text>
        <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
          <Text color={RADIX.COLOR.SECONDARY} size="6" weight="bold">
            {value ?? '-'}
          </Text>
          {showDelta && (
            <Badge color={RADIX.COLOR.DANGER}>
              {delta >= 0 ? '+' : ''}
              {feetFloatToString(delta)}
            </Badge>
          )}
        </Flex>
      </Flex>
    </Card>
  );
};

const DataListValue = ({ children }: PropsWithChildren) => (
  <DataList.Value>
    <Text weight="medium">{children}</Text>
  </DataList.Value>
);

interface IProps {
  machine: IAdminMachine;
  sendRemoteCalRequest: SendRemoteCalRequest;
}

export const RemoteVisionCalibrationVerification = ({
  machine,
  sendRemoteCalRequest,
}: IProps) => {
  const lastResponse = useVisionRemoteStore((s) => s.lastResponse);
  const leaderSliders = useVisionRemoteStore((s) => s.leaderSliders);
  const followerSliders = useVisionRemoteStore((s) => s.followerSliders);
  const saving = useVisionRemoteStore((s) => s.saving);

  const [dialogConfirm, setDialogConfirm] = useState<number>();

  const insetFt = MachineHelper.getWheelsInsetFt(machine.hardware_version);

  const currPlateDistanceFt = machine.releaseFt - insetFt;

  const onSave = useCallback(async () => {
    try {
      useVisionRemoteStore.getState().setSaving(true);

      await sendRemoteCalRequest({
        step: 'save',
        leader: leaderSliders,
        follower: followerSliders,
      });

      NotifyHelper.success({
        message_md: 'Calibration settings saved successfully',
      });

      setDialogConfirm(undefined);
      useVisionRemoteStore.getState().reset();
    } catch (error) {
      NotifyHelper.error({
        message_md: 'Failed to save calibration settings',
      });
    } finally {
      useVisionRemoteStore.getState().setSaving(false);
    }
  }, [leaderSliders, followerSliders]);

  const visionReleaseFt = lastResponse
    ? lastResponse.plate_distance_ft + insetFt
    : undefined;

  const deltaFt = lastResponse
    ? lastResponse.plate_distance_ft - currPlateDistanceFt
    : undefined;

  const absDeltaFt = isNil(deltaFt) ? undefined : Math.abs(deltaFt);

  return (
    <ErrorBoundary identifier="RemoteVisionCalibrationPlateDistance">
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD} height="100%">
        <Container size="1" flexShrink="1" overflow="auto">
          <Flex direction="column" gap="4">
            <RemoteVisionCalibrationStageHeader />
            <Grid gap={RADIX.FLEX.GAP.SM} columns="2">
              <DataCard
                title="Plate Distance"
                value={feetFloatToString(currPlateDistanceFt)}
              />
              <DataCard
                title="Vision Plate Distance"
                value={
                  lastResponse
                    ? feetFloatToString(lastResponse.plate_distance_ft)
                    : 'Loading...'
                }
                delta={deltaFt}
              />
              <DataCard
                title="Release Distance"
                value={feetFloatToString(machine.releaseFt)}
              />
              <DataCard
                title="Vision Release Distance"
                value={feetFloatToString(visionReleaseFt)}
                delta={deltaFt}
              />
              {!isNil(absDeltaFt) && (
                <Flex gridColumn="span 2">
                  {absDeltaFt <= VisionHelper.WARNING_DISTANCE_DELTA_FT ? (
                    // within spec
                    <CommonCallout
                      color={RADIX.COLOR.SUCCESS}
                      icon={
                        <Box mt="3px">
                          <CheckCircledIcon />
                        </Box>
                      }
                      flexAlign="start"
                      content={
                        <Flex direction="column">
                          <Text weight="medium">Calibration Accurate</Text>
                          <Text color={RADIX.COLOR.SECONDARY}>
                            The input plate distance matches the plate distance
                            to within 1 ft.
                          </Text>
                        </Flex>
                      }
                    />
                  ) : (
                    // outside spec
                    <CommonCallout
                      color={RADIX.COLOR.DANGER}
                      icon={
                        <Box mt="3px">
                          <ExclamationTriangleIcon />
                        </Box>
                      }
                      flexAlign="start"
                      content={
                        <Flex direction="column">
                          <Text weight="medium">
                            Distance mismatch detected
                          </Text>
                          <Text color={RADIX.COLOR.SECONDARY}>
                            The calculated distance differs by{' '}
                            {feetFloatToString(absDeltaFt)} from the expected
                            release distance. Try performing a stereo
                            calibration or contacting engineering if the problem
                            persists.
                          </Text>
                        </Flex>
                      }
                    />
                  )}
                </Flex>
              )}
            </Grid>
            {lastResponse && (
              <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
                <Text size="3" weight="medium">
                  Calibration Summary
                </Text>
                <Flex direction="column" gap={RADIX.FLEX.GAP.XS}>
                  <Text size="2" weight="medium">
                    Leader Camera
                  </Text>
                  <Card>
                    <Grid columns="3" gap={RADIX.FLEX.GAP.MD}>
                      <DataList.Root orientation="vertical">
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Roll
                          </DataList.Label>
                          <DataListValue>
                            {round(lastResponse.leader.roll_deg, 2)}°
                          </DataListValue>
                        </DataList.Item>
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Position X
                          </DataList.Label>
                          <DataListValue>
                            {feetFloatToString(
                              lastResponse.leader.x_mm * MM_TO_FT
                            )}
                          </DataListValue>
                        </DataList.Item>
                      </DataList.Root>
                      <DataList.Root orientation="vertical">
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Pitch
                          </DataList.Label>
                          <DataListValue>
                            {round(lastResponse.leader.pitch_deg, 2)}°
                          </DataListValue>
                        </DataList.Item>
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Y Translation
                          </DataList.Label>
                          <DataListValue>
                            {feetFloatToString(
                              lastResponse.leader.y_mm * MM_TO_FT
                            )}
                          </DataListValue>
                        </DataList.Item>
                      </DataList.Root>
                      <DataList.Root orientation="vertical">
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Yaw
                          </DataList.Label>
                          <DataListValue>
                            {round(lastResponse.leader.yaw_deg, 2)}°
                          </DataListValue>
                        </DataList.Item>
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Z Translation
                          </DataList.Label>
                          <DataListValue>
                            {feetFloatToString(
                              lastResponse.leader.z_mm * MM_TO_FT
                            )}
                          </DataListValue>
                        </DataList.Item>
                      </DataList.Root>
                    </Grid>
                  </Card>
                </Flex>
                <Flex direction="column" gap={RADIX.FLEX.GAP.XS}>
                  <Text size="2" weight="medium">
                    Follower Camera
                  </Text>
                  <Card>
                    <Grid columns="3" gap={RADIX.FLEX.GAP.SM}>
                      <DataList.Root orientation="vertical">
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Roll
                          </DataList.Label>
                          <DataListValue>
                            <Text weight="medium">
                              {round(lastResponse.follower.roll_deg, 2)}°
                            </Text>
                          </DataListValue>
                        </DataList.Item>
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Position X
                          </DataList.Label>
                          <DataListValue>
                            {feetFloatToString(
                              lastResponse.follower.x_mm * MM_TO_FT
                            )}
                          </DataListValue>
                        </DataList.Item>
                      </DataList.Root>
                      <DataList.Root orientation="vertical">
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Pitch
                          </DataList.Label>
                          <DataListValue>
                            {round(lastResponse.follower.pitch_deg, 2)}°
                          </DataListValue>
                        </DataList.Item>
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Y Translation
                          </DataList.Label>
                          <DataListValue>
                            {feetFloatToString(
                              lastResponse.follower.y_mm * MM_TO_FT
                            )}
                          </DataListValue>
                        </DataList.Item>
                      </DataList.Root>
                      <DataList.Root orientation="vertical">
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Yaw
                          </DataList.Label>
                          <DataListValue>
                            {round(lastResponse.follower.yaw_deg, 2)}°
                          </DataListValue>
                        </DataList.Item>
                        <DataList.Item>
                          <DataList.Label color={RADIX.COLOR.SECONDARY}>
                            Z Translation
                          </DataList.Label>
                          <DataListValue>
                            {feetFloatToString(
                              lastResponse.follower.z_mm * MM_TO_FT
                            )}
                          </DataListValue>
                        </DataList.Item>
                      </DataList.Root>
                    </Grid>
                  </Card>
                </Flex>
              </Flex>
            )}
          </Flex>
        </Container>
        <Flex ml="auto" gap={RADIX.FLEX.GAP.SM} mt="auto">
          <DialogButton
            label="Back"
            onClick={useVisionRemoteStore.getState().prevStage}
          />
          <DialogButton
            label="Complete Calibration"
            color={RADIX.COLOR.ACCENT}
            variant="solid"
            onClick={() => setDialogConfirm(Date.now())}
          />
        </Flex>
        {dialogConfirm && (
          <CommonConfirmationDialog
            key={dialogConfirm}
            identifier="VisionCalibrationApplyReleaseConfirmation"
            title="Are you sure?"
            content={
              <Text>
                This will save your Leader & Follower camera calibration
                settings.
              </Text>
            }
            action={{
              label: 'Continue',
              loading: saving,
              color: RADIX.COLOR.SUCCESS,
              onClick: async () => {
                if (isNil(visionReleaseFt)) {
                  NotifyHelper.warning({
                    message_md:
                      'Please wait for Vision to report a distance reading first.',
                  });
                  return;
                }

                await onSave();
              },
            }}
          />
        )}
      </Flex>
    </ErrorBoundary>
  );
};
